import { createContext, useCallback, useContext, useEffect, useId, useState } from "react";

type ReportPendingUploads = (uploaderId: string, hasPendingUploads: boolean) => void;

const PendingUploadsContext = createContext<ReportPendingUploads>(() => undefined);

export const PendingUploadsProvider = PendingUploadsContext.Provider;

export const usePendingUploadsTracker = () => {
  const [uploadersWithPendingUploads, setUploadersWithPendingUploads] = useState<ReadonlySet<string>>(new Set());

  const report = useCallback<ReportPendingUploads>((uploaderId, hasPendingUploads) => {
    setUploadersWithPendingUploads((current) => {
      if (current.has(uploaderId) === hasPendingUploads) return current;
      const next = new Set(current);
      if (hasPendingUploads) next.add(uploaderId);
      else next.delete(uploaderId);
      return next;
    });
  }, []);

  return { hasPendingUploads: uploadersWithPendingUploads.size > 0, report };
};

export const useReportPendingUploads = (hasPendingUploads: boolean) => {
  const report = useContext(PendingUploadsContext);
  const uploaderId = useId();

  useEffect(() => {
    report(uploaderId, hasPendingUploads);
  }, [report, uploaderId, hasPendingUploads]);

  useEffect(() => () => report(uploaderId, false), [report, uploaderId]);
};
